/**
 * dateTimeUtils.ts
 *
 * 카카오 모먼트 날짜/시간 관련 순수 유틸 함수 모음.
 *
 * 동일하거나 유사한 로직이 MomentCreatePage, MomentListPage, MomentReservePage에
 * 분산 정의되어 있었습니다. 이 파일에서 단일 관리합니다.
 *
 * 규칙:
 * - 모든 함수는 순수 함수입니다. 외부 상태를 읽거나 변경하지 않습니다.
 * - Date 객체는 로컬 타임존 기준으로 처리합니다.
 */

// ── 서버 날짜 파싱 ──────────────────────────────────────────────

/**
 * 서버에서 내려오는 sendDate 문자열("YYYY-MM-DD HH:mm:ss" 또는 ISO 형식)을
 * Date 객체로 변환합니다.
 *
 * - 공백 구분자("YYYY-MM-DD HH:mm:ss")와 T 구분자("YYYY-MM-DDTHH:mm:ss") 모두 처리
 * - 파싱 실패 시 16자리 잘라내기로 재시도 (일부 브라우저 호환성 대응)
 */
export function parseMomentSendDateTime(sendDate?: string | null): Date | null {
  if (!sendDate) return null;
  const normalized = sendDate.replace(" ", "T");
  const parsed = new Date(normalized);
  if (!Number.isNaN(parsed.getTime())) return parsed;
  // 일부 환경에서 초(ss) 없이도 파싱하도록 16자리 자르기 후 재시도
  const truncated = normalized.length >= 16 ? `${normalized.substring(0, 16)}:00` : normalized;
  const fallback = new Date(truncated);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

/**
 * 발송 예약 일시가 현재 시각 기준 10분 이후인지 확인합니다.
 * 발송 10분 전 이후에는 메시지 내용 수정이 불가능합니다.
 *
 * true → 아직 10분 이상 남아 있어 수정 가능
 * false → 10분 미만이거나 이미 지난 경우
 */
export function canEditReadyMessage(sendDate?: string | null): boolean {
  const sendDateTime = parseMomentSendDateTime(sendDate);
  if (!sendDateTime) return false;
  return sendDateTime.getTime() > Date.now() + 10 * 60 * 1000;
}

// ── 날짜 문자열 변환 ────────────────────────────────────────────

/**
 * Date 객체를 "YYYY-MM-DD" 형식의 로컬 날짜 문자열로 변환합니다.
 * toLocaleDateString() 대신 직접 포매팅하여 브라우저 로케일 영향을 받지 않도록 합니다.
 */
export function getLocalDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Date 객체를 "HH:mm" 형식의 로컬 시간 문자열로 변환합니다.
 */
export function getLocalTimeString(date: Date): string {
  const hh = String(date.getHours()).padStart(2, "0");
  const mm = String(date.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

// ── 발송 예약 시간 선택 옵션 ────────────────────────────────────

/**
 * 발송 가능한 시간 옵션 목록을 생성합니다.
 * 08:00 ~ 20:50 범위에서 5분 단위로 생성합니다.
 */
export function buildSendTimeOptions(): string[] {
  const options: string[] = [];
  for (let h = 8; h <= 20; h++) {
    for (let m = 0; m < 60; m += 5) {
      if (h === 20 && m > 50) break;
      const hh = String(h).padStart(2, "0");
      const mm = String(m).padStart(2, "0");
      options.push(`${hh}:${mm}`);
    }
  }
  return options;
}

export function isValidReservationTimeOption(sendTime: string): boolean {
  if (!sendTime || !/^\d{2}:\d{2}$/.test(sendTime)) return false;
  const [hour, minute] = sendTime.split(":").map(Number);
  if (Number.isNaN(hour) || Number.isNaN(minute)) return false;
  if (minute % 5 !== 0) return false;
  if (hour < 8 || hour > 20) return false;
  if (hour === 20 && minute > 50) return false;
  return true;
}

// ── 최소 예약 가능 일시 계산 ────────────────────────────────────

/**
 * 분 단위를 5분 단위로 올림합니다.
 * 예) 14:03 → 14:05, 14:05 → 14:05
 */
export function roundUpToNextFiveMinutes(date: Date): Date {
  const rounded = new Date(date);
  rounded.setSeconds(0, 0);
  const minutes = rounded.getMinutes();
  const remainder = minutes % 5;
  if (remainder !== 0) {
    rounded.setMinutes(minutes + (5 - remainder));
  }
  return rounded;
}

/**
 * 현재 시각 기준으로 예약 가능한 최소 일시를 반환합니다.
 * 현재 시각 + 10분에서 5분 단위로 올림한 값입니다.
 */
export function getMinReservationDateTime(): Date {
  const minDate = new Date();
  minDate.setMinutes(minDate.getMinutes() + 10);
  return roundUpToNextFiveMinutes(minDate);
}

// ── 예약 일시 파싱 / 검증 ────────────────────────────────────────

/**
 * 서버에서 받은 예약 일시 문자열을 { date, time } 형태로 분리합니다.
 * "YYYY-MM-DD HH:mm:ss" 또는 "YYYY-MM-DDTHH:mm:ss" 형식을 처리합니다.
 * 파싱 실패 시 null을 반환합니다.
 */
export function parseReservationDateTime(value: string): { date: string; time: string } | null {
  if (!value) return null;
  const normalized = value.trim().replace(" ", "T");
  const [datePart, timePart = ""] = normalized.split("T");
  if (!datePart) return null;
  const normalizedTime = timePart.substring(0, 5);
  if (!normalizedTime) return null;
  return { date: datePart, time: normalizedTime };
}

/**
 * 선택한 발송 날짜/시간이 최소 예약 가능 시간보다 이전인지 확인합니다.
 * true → 너무 이른 시간 (선택 불가)
 */
export function isBeforeMinReservationTime(sendDate: string, sendTime: string): boolean {
  if (!sendDate || !sendTime) return false;
  const selected = new Date(`${sendDate}T${sendTime}:00`);
  if (Number.isNaN(selected.getTime())) return false;
  return selected.getTime() < getMinReservationDateTime().getTime();
}

/**
 * 선택한 날짜 기준으로 선택 가능한 발송 시간 목록을 반환합니다.
 * 오늘 날짜인 경우 최소 예약 가능 시간 이후의 옵션만 반환합니다.
 *
 * @param sendDate 선택한 발송 날짜 ("YYYY-MM-DD")
 * @param minDateStr 오늘 날짜 문자열 ("YYYY-MM-DD") — 당일 여부 판단용
 */
export function getAvailableSendTimeOptions(sendDate: string, minDateStr: string): string[] {
  const allOptions = buildSendTimeOptions();
  if (sendDate !== minDateStr) return allOptions;
  const minTime = getLocalTimeString(getMinReservationDateTime());
  return allOptions.filter((time) => time >= minTime);
}
